import EmberService, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import moment from 'moment';

import type { APIData, Service, Timeframe } from 'webapp-types';
import { TimeframeType } from 'webapp/enums/enums';

import GoalsTimeframePopupComponent from 'webapp/components/goals/timeframe/popup';

import type { FilterOption } from 'webapp/models-custom/filter-sort/filter';
import type Filter from 'webapp/models-custom/filter-sort/filter';

export default class TimeframeService extends EmberService {
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('request') declare requestService: Service.Request;
	@service('popup') declare popupService: Service.Popup;

	@tracked private timeframePeriodsCache: Record<
		number,
		| APIData.Goal.TimeframePeriods.Response
		| null
		| Promise<APIData.Goal.TimeframePeriods.Response | null>
	> = {};

	getTimeframePeriods(
		timeframe: Timeframe,
	): Promise<APIData.Goal.TimeframePeriods.Response | null> {
		const key = timeframe.year;
		const cached = this.timeframePeriodsCache[key];
		if (cached instanceof Promise) {
			return cached;
		} else if (cached) {
			return Promise.resolve(cached);
		} else {
			const payload: APIData.Goal.TimeframePeriods.Request = { year: timeframe.year };

			const promise = this.requestService
				.api<APIData.Goal.TimeframePeriods.Response>(
					'POST',
					'/goals/timeframe-periods',
					payload,
				)
				.then((reply) => {
					this.timeframePeriodsCache[key] = reply;
					return reply;
				})
				.catch((_error) => {
					delete this.timeframePeriodsCache[key];
					return null;
				});

			void promise.finally(() => {
				// trigger tracked change
				this.timeframePeriodsCache = this.timeframePeriodsCache;
			});

			this.timeframePeriodsCache[key] = promise;

			return promise;
		}
	}

	invalidateTimeframePeriodsCache(): void {
		this.timeframePeriodsCache = {};
	}

	getTimeframePeriodsFromCache(
		timeframe: Timeframe,
	): APIData.Goal.TimeframePeriods.Response | null {
		const cached = this.timeframePeriodsCache[timeframe.year];
		if (cached instanceof Promise || !cached) {
			return null;
		}
		return cached;
	}

	findPeriod(
		response: APIData.Goal.TimeframePeriods.Response,
		timeframeType: number,
	): APIData.Goal.TimeframePeriods.PeriodDateRange | null {
		const item = response.find((entry) => entry.timeframeType === timeframeType);
		return item?.periodDateRange ?? null;
	}

	async getTimeframePeriodsLabel(timeframe: Timeframe, format = 'MMM D, YYYY') {
		const response = await this.getTimeframePeriods(timeframe);
		const periodData = response ? this.findPeriod(response, timeframe.type) : null;
		if (!periodData) {
			return '';
		}
		const timeZone = this.currentUserService.model?.timezone || 'Etc/UTC';
		const formatFn = (ts: number) => moment.unix(ts).tz(timeZone).format(format);
		const startDate = formatFn(periodData.periodStartTS);
		const endDate = formatFn(periodData.periodEndTS - 1);
		return `${startDate} - ${endDate}`;
	}

	getTimeframeString(timeframe: Timeframe | null, addYear = false) {
		let label = '';
		if (timeframe) {
			const shortYearText = `FY${timeframe.year % 100}`;
			if (timeframe.type === TimeframeType.Y) {
				label = shortYearText;
			} else {
				switch (timeframe.type) {
					case TimeframeType.H1:
						label = 'H1';
						break;
					case TimeframeType.H2:
						label = 'H2';
						break;
					case TimeframeType.Q1:
						label = 'Q1';
						break;
					case TimeframeType.Q2:
						label = 'Q2';
						break;
					case TimeframeType.Q3:
						label = 'Q3';
						break;
					case TimeframeType.Q4:
						label = 'Q4';
						break;
				}
				if (addYear) {
					label += ` ${shortYearText}`;
				}
			}
		} else {
			label = 'Continuous';
		}
		return label;
	}

	getTimeframeTypeStringForTracking(timeframe?: TimeframeType) {
		switch (timeframe) {
			case TimeframeType.Y:
				return 'FY';
			case TimeframeType.H1:
			case TimeframeType.H2:
				return 'H';
			case TimeframeType.Q1:
			case TimeframeType.Q2:
			case TimeframeType.Q3:
			case TimeframeType.Q4:
				return 'Q';
			default:
				return 'no timeframe';
		}
	}

	isMatchingTimeframe(
		item: { timeframe: Timeframe | null }, // goal or forecast
		givenTimeframe: Timeframe | null,
	) {
		if (givenTimeframe) {
			if (item.timeframe?.year === givenTimeframe.year) {
				const itemTimeframeType = item.timeframe.type;
				if (givenTimeframe.type === TimeframeType.Y) {
					return true;
				} else if (itemTimeframeType === givenTimeframe.type) {
					return true;
				} else if ([TimeframeType.Q1, TimeframeType.Q2].includes(itemTimeframeType)) {
					return givenTimeframe.type === TimeframeType.H1;
				} else if ([TimeframeType.Q3, TimeframeType.Q4].includes(itemTimeframeType)) {
					return givenTimeframe.type === TimeframeType.H2;
				}
			}
		} else {
			// perpetual
			return !item.timeframe;
		}
		return false;
	}

	// ------ Timeframe filtering (filter-sort) specifics ------

	getStringForFilterFromTimeframe(timeframe: Timeframe | null): string {
		if (timeframe) {
			return `${timeframe.year}-${timeframe.type}`;
		} else {
			return 'p'; // perpetual
		}
	}

	getTimeframeFromStringForFilter(timeframeString: string): Timeframe | null | undefined {
		if (timeframeString === 'p') {
			return null;
		} else {
			const [year, type] = timeframeString.split('-');
			if (year && type) {
				return {
					year: Number(year),
					type: Number(type),
				};
			} else {
				return undefined;
			}
		}
	}

	// used for both goal and client goals filters
	getTimeframeOptionsForFilter(timeframeFilter?: Filter<unknown, string>) {
		const list: FilterOption<string>[] = [
			{ id: 'all', text: 'All' },
			{ id: '', text: 'Unknown' },
		];

		// we only need the current selected to show correctly
		// bcos the popup is custom and does not need all list
		if (timeframeFilter && !timeframeFilter.isDefaultSelected) {
			const selectedValue = timeframeFilter.selected[0];
			if (selectedValue) {
				const selectedTimeframe = this.getTimeframeFromStringForFilter(selectedValue);
				if (selectedTimeframe !== undefined) {
					list.push({
						id: selectedValue,
						text: this.getTimeframeString(selectedTimeframe, true),
					});
				}
			}
		}
		return list;
	}

	// used for both goal and client goals filters
	openTimeframePopupForFilter(
		timeframeFilter: Filter<unknown, string>,
		attachTo: HTMLElement,
		theme?: string,
	) {
		const selectedTimeframe = this.getTimeframeFromStringForFilter(
			timeframeFilter.selected[0]!,
		);

		const popup = this.popupService.createContext(GoalsTimeframePopupComponent, attachTo, {
			popupClass: 'popup-type-timeframe',
			theme,
		});

		popup.open({
			selected: selectedTimeframe,
			onChange: (timeframe: Timeframe | null) => {
				const string = this.getStringForFilterFromTimeframe(timeframe);
				timeframeFilter.selectOption(string);
			},
			onClear: () => {
				timeframeFilter.selectOption('all');
			},
			disablePast: false,
		});

		return popup;
	}

	getCurrentQuarter(): TimeframeType {
		const currentMonth = moment().month() + 1; // returns 0-11
		if (currentMonth > 0 && currentMonth <= 3) {
			return TimeframeType.Q1;
		} else if (currentMonth > 3 && currentMonth <= 6) {
			return TimeframeType.Q2;
		} else if (currentMonth > 6 && currentMonth <= 9) {
			return TimeframeType.Q3;
		} else {
			return TimeframeType.Q4;
		}
	}
}

declare module '@ember/service' {
	interface Registry {
		timeframe: TimeframeService;
	}
}
